import { useEffect, useState } from 'react';
import AddQuickFiltersPopoverContent from './AddQuickFiltersPopoverContent';
import EditQuickFiltersPopoverContent from './EditQuickFiltersPopoverContent';
import FormattedMessage from 'I18n/components/FormattedMessage';
import MoreQuickFiltersPopoverContent from './MoreQuickFiltersPopoverContent';
import QuickFilterSelectBase from './QuickFilterSelectBase';
import { Button } from 'foundations-components/transitional/components/Button';
import { AddIcon, DownCaratIcon } from 'foundations-assets';
import { Popover } from 'foundations-components/transitional/components/Popover';
import { syntheticEvent } from 'foundations-components/transitional/utils/SyntheticEvent';
import { Flex } from 'foundations-components/components/Flex';
import styled from 'foundations-theming/utils';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
// Hack: the foundation component Select and the UISelect use a 8px icon
// to prevent differnt looking quick filters we need to override the internal
// size of the icon
const TrellisMigrateIconResizer = styled.div.withConfig({
  displayName: "MoreQuickFiltersButton__TrellisMigrateIconResizer",
  componentId: "sdghd4-0"
})(["svg{width:8px !important;height:8px !important;margin-left:2px;margin-right:2px;}"]);
export const POPOVER_CONTENTS = {
  ADD_QUICK_FILTERS: 'ADD_QUICK_FILTERS',
  EDIT_QUICK_FILTERS: 'EDIT_QUICK_FILTERS',
  MORE_QUICK_FILTERS: 'MORE_QUICK_FILTERS'
};
const MoreQuickFiltersButton = ({
  allPropertyNames,
  buttonProperties,
  customPropertyNames,
  enableCustomFilters,
  maxCustomQuickFilters,
  enableReordering,
  overflowingQuickFiltersComponents,
  isActive,
  isQuickFiltersVisibilityCalculated,
  onCustomPropertyNamesChange
}) => {
  const [currentPopoverContent, setCurrentPopoverContent] = useState('');
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [hasUpdatedPropertyNames, setHasUpdatedPropertyNames] = useState(false);
  const hasOverflowingQuickFilters = overflowingQuickFiltersComponents.length > 0;
  useEffect(() => {
    if (hasOverflowingQuickFilters && hasUpdatedPropertyNames && isQuickFiltersVisibilityCalculated) {
      setCurrentPopoverContent(POPOVER_CONTENTS.MORE_QUICK_FILTERS);
      setHasUpdatedPropertyNames(false);
      setIsPopoverOpen(true);
    }
  }, [hasOverflowingQuickFilters, hasUpdatedPropertyNames, isQuickFiltersVisibilityCalculated, onCustomPropertyNamesChange]);
  const shouldHideButton = !hasOverflowingQuickFilters && (enableCustomFilters === false || !enableCustomFilters && !onCustomPropertyNamesChange);
  if (shouldHideButton) {
    return null;
  }
  const onTogglePopover = evt => {
    if (enableCustomFilters === true && !onCustomPropertyNamesChange) {
      return;
    }
    const newIsPopoverOpen = evt.target.value;
    setIsPopoverOpen(newIsPopoverOpen);
    if (!newIsPopoverOpen) {
      setCurrentPopoverContent(POPOVER_CONTENTS.MORE_QUICK_FILTERS);
    }
  };
  const onClosePopover = () => {
    onTogglePopover(syntheticEvent(false));
  };
  const onAddCustomPropertyName = newPropertyName => {
    if (onCustomPropertyNamesChange) {
      onCustomPropertyNamesChange([...customPropertyNames, newPropertyName]);
      setHasUpdatedPropertyNames(true);
    }
  };
  const getPopoverContent = () => {
    const hasDynamicQuickFiltersEnabled = enableCustomFilters !== false && !!onCustomPropertyNamesChange;
    if (hasDynamicQuickFiltersEnabled) {
      const shouldRenderEditQuickFiltersPopoverContent = customPropertyNames.length === maxCustomQuickFilters && !hasOverflowingQuickFilters;
      if (shouldRenderEditQuickFiltersPopoverContent || currentPopoverContent === POPOVER_CONTENTS.EDIT_QUICK_FILTERS) {
        return /*#__PURE__*/_jsx(EditQuickFiltersPopoverContent, {
          buttonProperties: buttonProperties,
          customPropertyNames: customPropertyNames,
          enableReordering: enableReordering,
          hasOverflowingQuickFilters: hasOverflowingQuickFilters,
          onCustomPropertyNamesChange: onCustomPropertyNamesChange,
          setCurrentPopoverContent: setCurrentPopoverContent,
          maxCustomQuickFilters: maxCustomQuickFilters
        });
      }
      if (!hasOverflowingQuickFilters || currentPopoverContent === POPOVER_CONTENTS.ADD_QUICK_FILTERS) {
        return /*#__PURE__*/_jsx(AddQuickFiltersPopoverContent, {
          allPropertyNames: allPropertyNames,
          customPropertyNames: customPropertyNames,
          hasOverflowingQuickFilters: hasOverflowingQuickFilters,
          onAddCustomPropertyName: onAddCustomPropertyName,
          onClosePopover: onClosePopover,
          setCurrentPopoverContent: setCurrentPopoverContent
        });
      }
    }
    return /*#__PURE__*/_jsx(MoreQuickFiltersPopoverContent, {
      customPropertyNames: customPropertyNames,
      overflowingQuickFiltersComponents: overflowingQuickFiltersComponents,
      hasDynamicQuickFiltersEnabled: hasDynamicQuickFiltersEnabled,
      setCurrentPopoverContent: setCurrentPopoverContent,
      maxCustomQuickFilters: maxCustomQuickFilters
    });
  };
  const getButtonContent = () => {
    return /*#__PURE__*/_jsxs(Flex, {
      alignItems: "center",
      gap: "100",
      children: [!hasOverflowingQuickFilters && /*#__PURE__*/_jsx(AddIcon, {
        color: "icon-core-subtle"
      }), /*#__PURE__*/_jsx(FormattedMessage, {
        message: "customerDataFiltersUI.quickFiltersBar.more"
      }), hasOverflowingQuickFilters &&
      /*#__PURE__*/
      // @bend-expect custom-component-unexpected-styled-components The cartet icon is 8x8 but the plus sign is 12x12, so we have to set 4px of horizontal margin to prevent layout shifting
      _jsx(TrellisMigrateIconResizer, {
        children: /*#__PURE__*/_jsx(DownCaratIcon, {
          color: "icon-core-subtle"
        })
      })]
    });
  };
  return /*#__PURE__*/_jsx(Popover, {
    closeOnOutsideClick: true,
    content: getPopoverContent(),
    onOpenChange: onTogglePopover,
    open: isPopoverOpen,
    placement: "bottom",
    children: /*#__PURE__*/_jsx(QuickFilterSelectBase, {
      button: /*#__PURE__*/_jsx(Button, {
        "data-selenium-test": "FiltersBar-more",
        onClick: () => onTogglePopover(syntheticEvent(!isPopoverOpen)),
        size: buttonProperties.BUTTON_SIZE,
        use: buttonProperties.BUTTON_USE,
        children: getButtonContent()
      }),
      buttonHeight: buttonProperties.BUTTON_HEIGHT,
      isActive: isActive,
      propertyName: "more"
    })
  });
};
export default MoreQuickFiltersButton;